/* eslint-disable @typescript-eslint/no-non-null-assertion */

import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule, DatePipe } from '@angular/common'; // Import CommonModule
import { Component } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu'; // Import MatMenuModule
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip'; // Import MatTooltipModule
import { Router } from '@angular/router';
import { Conversation } from '@app-libs/model';
import { LoadingSpinnerComponent } from '../../common/components/loading-spinner';
import { ConversationsApiService } from '../../services/api/conversations-api.service';
import { AuthService } from '../../services/auth/auth.service';
import { ChatCardComponent } from '../common/chat-card/chat-card.component';
import { DataTableComponent } from '../common/data-table/data-table.component';
import { SearchBarComponent } from '../common/search-bar/search-bar.component';

@Component({
  selector: 'app-conversations',
  imports: [
    MatCardModule,
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatMenuModule,
    ReactiveFormsModule,
    SearchBarComponent,
    DataTableComponent,
    ChatCardComponent,
    LoadingSpinnerComponent
  ],
  providers: [DatePipe],
  templateUrl: './conversations.component.html',
  styleUrls: [
    './conversations.component.scss',
    '../assistants/assistants.component.scss',
    '../common/data-table/data-table.component.scss',
  ],
  animations: [
    trigger('fade', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('600ms ease-in', style({ opacity: 1 }))]),
    ]),
  ],
})
export class ConversationsComponent {
  conversations: Conversation[] = [];
  filteredConversations: any[] = []; // Will contain processed conversations with secondaryInfo
  loading = true;
  searchPlaceholder = 'Search for conversations ';
  searchTooltip =
    'Search for conversations by title, assistant, or last message date';

  // Presentation mode for data-table (can be changed to test different layouts)
  presentationMode: 'minimal' | 'striped' | 'cards' = 'minimal';

  // For data table component
  columns = [
    {
      key: 'description',
      header: 'Continue past conversations',
      isDetails: true,
      fields: ['title', 'secondaryInfo'],
      classes: ['name-col', 'description-col']
    },
    {
      key: 'action',
      header: '',
      isAction: true,
      actionIcon: 'chat_bubble_outline',
      tooltip: 'Continue conversation'
    }
  ];

  searchControl = new FormControl('');

  // Simple cache for formatted dates to prevent re-computation
  private dateFormatCache = new Map<string, string>();

  constructor(
    private router: Router,
    private conversationService: ConversationsApiService,
    private auth: AuthService,
    private snackBar: MatSnackBar,
    private datePipe: DatePipe
  ) {
    // this.searchControl.valueChanges.subscribe(value => this.filterAssistants(value || ''));
    this.initializeConversations();
  }

  async initializeConversations() {
    // this.conversations = await this.conversationService.fetchConversationsByUserId("123", 100);
    try {
      const userId = this.auth.getCurrentUserId();
      if (userId) {
        this.conversations =
          (await this.conversationService
            .fetchConversationsForUserWorkspace(userId, 200)
            .toPromise()) || [];
      } else {
        throw new Error('User ID is null or undefined');
      }
      //fetchConversationsCurrentUser(100).toPromise();
      this.filteredConversations = this.processConversationsForDataTable(this.conversations);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    } finally {
      this.loading = false;
    }
  }

  // Process conversations to create data-table compatible format
  private processConversationsForDataTable(conversations: Conversation[]) {
    return conversations.map(conversation => {
      const formattedDate = this.getFormattedDate(conversation.last_message_date);
      const assistantName = (conversation as any).assistant_name || 'Chat';

      return {
        ...conversation,
        // Create simple string for secondary info - data-table will display this as-is
        secondaryInfo: `${assistantName} • ${formattedDate}`
      };
    });
  }

  // Cached date formatting to prevent re-computation
  private getFormattedDate(dateValue: any): string {
    if (!dateValue) return '';

    const dateKey = dateValue.toString();
    if (this.dateFormatCache.has(dateKey)) {
      return this.dateFormatCache.get(dateKey)!;
    }

    const formatted = this.datePipe.transform(dateValue, 'MMM d y, H:mm') || '';
    this.dateFormatCache.set(dateKey, formatted);
    return formatted;
  }

  filterAssistants(query: string) {
    console.debug('Filtering Conversations with query:', query);
    const lowerQuery = query.toLowerCase();
    const filtered = this.conversations?.filter(
      (conversation) =>
        conversation.assistant_id?.toLowerCase().includes(lowerQuery) ||
        conversation.title?.toLowerCase().includes(lowerQuery) ||
        conversation.last_message_date
          ?.toString()
          .toLowerCase()
          .includes(lowerQuery)
    );
    // Process filtered results for data-table
    this.filteredConversations = this.processConversationsForDataTable(filtered || []);
    console.debug('Filtered Conversations:', this.filteredConversations);
  }

  async importConversation(event: any, conversation: Conversation) {
    console.debug(`Importing conversation ${conversation.conversation_id}`);

    const chatType: string = conversation.assistant_id ? 'assistant' : 'chat';

    event.preventDefault();
    event.stopPropagation();

    console.debug(
      `Navigating to chat with conversation ${conversation.conversation_id} and type ${chatType}`
    );
    this.router.navigate(
      ['/conversations/chat', conversation.conversation_id],
      { state: { chatType: chatType } }
    );
  }

  onRowClick(event: any) {
    console.log('Row clicked:', event);
    this.importConversation(event.event, event.row);
  }

  onActionClick(event: any) {
    console.log('Action clicked:', event);
    this.importConversation(event.event, event.row);
  }
}
